import React from 'react';
import { makeStyles } from '@fluentui/react-components';
import { 
    Carousel, 
    CarouselViewport,
    CarouselSlider,
    CarouselNav,
    CarouselNavButton,
    CarouselNavContainer
} from '@fluentui/react-components';

const useStyles = makeStyles({
    carouselSection: {
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '40px',
        display: 'flex',
    },

    carouselWrapper: {
        alignSelf: 'stretch',
        '@media (max-width: 768px)': {
            paddingLeft: '0px',
        },
    },

    carouselHeader: {
        alignSelf: 'stretch',
        paddingLeft: '80px',
        paddingRight: '80px',
        marginBottom: '40px',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
        gap: '32px',
        display: 'flex',
        '@media (max-width: 768px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
    },

    carouselTitle: {
        flex: '1 1 0',
        color: '#222222',
        fontSize: '64px',
        fontFamily: 'Figtree',
        fontWeight: 400,
        lineHeight: '64px',
        wordWrap: 'break-word',
        '@media (max-width: 768px)': {
            fontSize: '32px',
            lineHeight: '32px',
            textAlign: 'center',
        },
    },

    navigationButtons: {
        width: '112px',
        height: '48px',
        justifyContent: 'space-between',
        alignItems: 'center',
        display: 'flex',
        '@media (max-width: 768px)': {
            display: 'none',
        },
        '& > div[role="tablist"]': {
            display: 'none',
        },
    },

    carouselSlider: {
        '& > *:first-child': {
            marginLeft: '80px',
        },
        '@media (max-width: 768px)': {
            '& > *:first-child': {
                marginLeft: '16px',
            },
        },
    },
});

interface AlyzeCarouselProps {
    title: string;
    children: React.ReactNode;
    ariaLabelNext?: string;
    ariaLabelPrev?: string;
    ariaLabelItem?: (index: number) => string;
}

export const AlyzeCarousel: React.FC<AlyzeCarouselProps> = ({
    title,
    children,
    ariaLabelNext = 'go to next',
    ariaLabelPrev = 'go to previous',
    ariaLabelItem = (index) => `Item ${index + 1}`,
}) => {
    const styles = useStyles();

    return (
        <div className={styles.carouselSection}>
            <div className={styles.carouselWrapper}>
                <Carousel 
                    groupSize={1}
                    circular={false}
                    draggable
                >
                    <div className={styles.carouselHeader}>
                        <div className={styles.carouselTitle}>{title}</div>
                        <CarouselNavContainer
                            className={styles.navigationButtons}
                            layout="inline"
                            next={{ "aria-label": ariaLabelNext }}
                            prev={{ "aria-label": ariaLabelPrev }}
                        >
                            <CarouselNav>
                                {(index) => (
                                    <CarouselNavButton
                                        aria-label={ariaLabelItem(index)}
                                    />
                                )}
                            </CarouselNav>
                        </CarouselNavContainer>
                    </div>
                    <CarouselViewport>
                        <CarouselSlider className={styles.carouselSlider}>
                            {children}
                        </CarouselSlider>
                    </CarouselViewport>
                </Carousel>
            </div>
        </div>
    );
};
